import type { BankTxStatus, ExpenseKind } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, DatePicker, Form, Input, Modal, Popconfirm, Segmented, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import { DateText, MoneyInput, MoneyText } from '../../lib/format';
import { fetchBankAccounts } from '../firme/api';
import {
  addCheltuialaPayment,
  createCheltuiala,
  fetchCheltuieli,
  fetchExpenseBankTransactions,
  fetchExpenseCategories,
  ignoreBankTransaction,
  unignoreBankTransaction,
  type ExpenseBankTx,
  type ExpenseListItem,
} from './api';
import { ExpenseStatusTag, KIND_LABELS } from './tags';

const { Text } = Typography;
const PAGE_SIZE = 10;

type TabKey = Extract<BankTxStatus, 'UNMATCHED' | 'IGNORED' | 'MATCHED'>;

interface AllocateFormValues {
  amountRon: string;
}

interface QuickCreateFormValues {
  kind: ExpenseKind;
  categoryId?: string;
  description?: string;
}

/**
 * "Tranzacții nereconciliate" — the stored DEBIT rows that still need a home:
 * allocate one to an existing expense (partial amounts allowed), turn it into
 * a brand-new expense (fees/taxes/salaries), or ignore it. The IGNORED tab
 * lets a mistake be undone; MATCHED shows where the money went.
 */
export function ReconciliereModal({
  open,
  firmaId,
  onClose,
  onChanged,
}: {
  open: boolean;
  firmaId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>('UNMATCHED');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [bankAccountId, setBankAccountId] = useState<string | undefined>(undefined);
  const [allocateTx, setAllocateTx] = useState<ExpenseBankTx | null>(null);
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | undefined>(undefined);
  const [createTx, setCreateTx] = useState<ExpenseBankTx | null>(null);
  const [expenseSearch, setExpenseSearch] = useState('');
  const [allocateForm] = Form.useForm<AllocateFormValues>();
  const [createForm] = Form.useForm<QuickCreateFormValues>();

  const filterParams = {
    status: tab,
    search: search || undefined,
    bankAccountId,
    dateFrom: range?.[0]?.format('YYYY-MM-DD'),
    dateTo: range?.[1]?.format('YYYY-MM-DD'),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['expense-bank-tx', firmaId, { ...filterParams, page }],
    queryFn: () => fetchExpenseBankTransactions({ companyId: firmaId, page, pageSize: PAGE_SIZE, ...filterParams }),
    enabled: open && !!firmaId,
  });

  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-accounts', firmaId],
    queryFn: () => fetchBankAccounts(firmaId),
    enabled: open && !!firmaId,
  });

  // Candidates for manual allocation: ANY expense (a settled receipt can be
  // linked to its card debit too — the link removes workspace noise without
  // changing its already-PAID status).
  const { data: expensesPage } = useQuery({
    queryKey: ['expenses', firmaId, { forAllocation: true, search: expenseSearch }],
    queryFn: () =>
      fetchCheltuieli({ companyId: firmaId, page: 1, pageSize: 20, search: expenseSearch || undefined }),
    enabled: open && allocateTx !== null,
  });

  const { data: categories } = useQuery({
    queryKey: ['expense-categories', firmaId],
    queryFn: () => fetchExpenseCategories(firmaId),
    enabled: open && createTx !== null,
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['expense-bank-tx'] });
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
    onChanged();
  }

  function reportError(error: unknown, fallback: string) {
    message.error(getApiError(error)?.message ?? fallback);
  }

  // The API serializes `@db.Date` columns as full ISO timestamps
  // ("2026-07-30T00:00:00.000Z"), but the payment/expense schemas require a
  // bare "YYYY-MM-DD" — so a date coming back from the API must be trimmed
  // before it is sent to another endpoint.
  const apiDate = (value: string) => dayjs(value).format('YYYY-MM-DD');

  const allocateMutation = useMutation({
    mutationFn: (values: AllocateFormValues) => {
      const tx = allocateTx!;
      return addCheltuialaPayment(selectedExpenseId!, {
        bankTransactionId: tx.id,
        paidOn: apiDate(tx.transactionDate),
        amountRon: values.amountRon,
      });
    },
    onSuccess: () => {
      message.success('Plata a fost alocată.');
      setAllocateTx(null);
      setSelectedExpenseId(undefined);
      refresh();
    },
    onError: (error) => reportError(error, 'Alocarea a eșuat.'),
  });

  const quickCreateMutation = useMutation({
    mutationFn: async (values: QuickCreateFormValues) => {
      const tx = createTx!;
      // Two steps in the expense's own terms: record the expense (not
      // settled), then allocate the full unallocated amount — PAID is derived
      // from the allocation, and stays honest if it is ever deleted.
      const expense = await createCheltuiala({
        companyId: firmaId,
        kind: values.kind,
        categoryId: values.categoryId,
        docDate: apiDate(tx.transactionDate),
        currency: 'RON',
        totalAmount: tx.remainingRon,
        description: (values.description ?? tx.description ?? '').slice(0, 500) || undefined,
        settled: false,
      });
      return addCheltuialaPayment(expense.id, {
        bankTransactionId: tx.id,
        paidOn: apiDate(tx.transactionDate),
        amountRon: tx.remainingRon,
      });
    },
    onSuccess: () => {
      message.success('Cheltuiala a fost creată și plata alocată.');
      setCreateTx(null);
      refresh();
    },
    onError: (error) => reportError(error, 'Cheltuiala nu a putut fi creată.'),
  });

  const ignoreMutation = useMutation({
    mutationFn: (txId: string) => ignoreBankTransaction(txId),
    onSuccess: () => refresh(),
    onError: (error) => reportError(error, 'Tranzacția nu a putut fi ignorată.'),
  });

  const unignoreMutation = useMutation({
    mutationFn: (txId: string) => unignoreBankTransaction(txId),
    onSuccess: () => refresh(),
    onError: (error) => reportError(error, 'Tranzacția nu a putut fi reactivată.'),
  });

  const columns: ColumnsType<ExpenseBankTx> = [
    { title: 'Data', key: 'transactionDate', width: 100, render: (_, row) => <DateText value={row.transactionDate} /> },
    { title: 'Sumă', key: 'amount', width: 110, align: 'right', render: (_, row) => <MoneyText value={row.amount} /> },
    {
      title: 'Beneficiar / descriere',
      key: 'who',
      ellipsis: true,
      render: (_, row) => (
        <Tooltip title={`${row.counterpartyName ?? ''} ${row.description ?? ''}`.trim()}>
          <span>{row.counterpartyName ?? row.description ?? '—'}</span>
          {row.counterpartyName && row.description ? (
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }} ellipsis>
              {row.description}
            </Text>
          ) : null}
        </Tooltip>
      ),
    },
    {
      title: tab === 'MATCHED' ? 'Alocat către' : 'Rest nealocat',
      key: 'remaining',
      width: tab === 'MATCHED' ? 240 : 130,
      align: tab === 'MATCHED' ? 'left' : 'right',
      render: (_, row) =>
        tab === 'MATCHED' ? (
          <Space direction="vertical" size={0}>
            {row.allocations.map((allocation) => (
              <Text key={allocation.id} style={{ fontSize: 12 }}>
                {allocation.expense.docNumber
                  ? `Doc. ${allocation.expense.docNumber}`
                  : (allocation.expense.description ?? KIND_LABELS[allocation.expense.kind])}
                {allocation.expense.supplier ? ` — ${allocation.expense.supplier.name}` : ''} (
                <MoneyText value={allocation.amountRon} />)
              </Text>
            ))}
          </Space>
        ) : (
          <MoneyText value={row.remainingRon} />
        ),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: tab === 'UNMATCHED' ? 300 : 130,
      render: (_, row) => {
        if (tab === 'IGNORED') {
          return (
            <Button size="small" onClick={() => unignoreMutation.mutate(row.id)}>
              Reactivează
            </Button>
          );
        }
        if (tab === 'MATCHED') {
          return <Tag color="green">Reconciliată</Tag>;
        }
        return (
          <Space size={4} wrap>
            <Button
              size="small"
              type="primary"
              onClick={() => {
                setAllocateTx(row);
                setSelectedExpenseId(undefined);
                setExpenseSearch('');
                allocateForm.setFieldsValue({ amountRon: row.remainingRon });
              }}
            >
              Alocă
            </Button>
            <Button
              size="small"
              onClick={() => {
                setCreateTx(row);
                createForm.setFieldsValue({ kind: 'OTHER', categoryId: undefined, description: row.description ?? undefined });
              }}
            >
              Cheltuială nouă
            </Button>
            <Popconfirm
              title="Ignori tranzacția?"
              description="Dispare din lista de reconciliere; o poți reactiva din fila Ignorate."
              okText="Ignoră"
              cancelText="Renunță"
              onConfirm={() => ignoreMutation.mutate(row.id)}
            >
              <Button size="small">Ignoră</Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const expenseColumns: ColumnsType<ExpenseListItem> = [
    { title: 'Data', key: 'docDate', width: 100, render: (_, row) => <DateText value={row.docDate} /> },
    {
      title: 'Document',
      key: 'document',
      width: 150,
      ellipsis: true,
      render: (_, row) => (row.docNumber ? row.docNumber : (row.description ?? KIND_LABELS[row.kind])),
    },
    { title: 'Furnizor', key: 'supplier', ellipsis: true, render: (_, row) => row.supplier?.name ?? '—' },
    {
      title: 'Total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (_, row) => <MoneyText value={row.totalAmount} currency={row.currency} />,
    },
    { title: 'Status', key: 'status', width: 130, render: (_, row) => <ExpenseStatusTag status={row.paymentStatus} /> },
  ];

  return (
    <Modal
      title="Tranzacții bancare (plăți)"
      open={open}
      onCancel={onClose}
      width="80vw"
      style={{ top: '8vh' }}
      destroyOnHidden
      footer={[
        <Button key="close" onClick={onClose}>
          Închide
        </Button>,
      ]}
    >
      <Space wrap style={{ marginBottom: 12 }} size="middle">
        <Segmented<TabKey>
          options={[
            { label: 'Nereconciliate', value: 'UNMATCHED' },
            { label: 'Reconciliate', value: 'MATCHED' },
            { label: 'Ignorate', value: 'IGNORED' },
          ]}
          value={tab}
          onChange={(value) => {
            setTab(value);
            setPage(1);
          }}
        />
        <Input.Search
          placeholder="Caută după beneficiar sau descriere"
          allowClear
          style={{ width: 280 }}
          onSearch={(value) => {
            setSearch(value);
            setPage(1);
          }}
        />
        <DatePicker.RangePicker
          value={range}
          format="DD.MM.YYYY"
          placeholder={['De la', 'Până la']}
          presets={[
            { label: 'Luna aceasta', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
            { label: 'Luna trecută', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
            { label: 'Anul acesta', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
          ]}
          onChange={(values) => {
            setRange(values && values[0] && values[1] ? [values[0], values[1]] : null);
            setPage(1);
          }}
        />
        <Select
          allowClear
          style={{ width: 260 }}
          placeholder="Toate conturile"
          value={bankAccountId}
          onChange={(value: string | undefined) => {
            setBankAccountId(value);
            setPage(1);
          }}
          options={(bankAccounts ?? []).map((account) => ({
            value: account.id,
            label: `${account.bankName} — ${account.iban}`,
          }))}
        />
      </Space>
      <Table<ExpenseBankTx>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data?.items ?? []}
        loading={isLoading}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: data?.total ?? 0, onChange: setPage, showSizeChanger: false }}
        locale={{
          emptyText:
            tab === 'UNMATCHED'
              ? 'Nicio tranzacție nereconciliată — importă un extras bancar din pagina Cheltuieli.'
              : 'Nimic aici.',
        }}
      />

      <Modal
        title="Alocă plata unei cheltuieli"
        open={allocateTx !== null}
        onCancel={() => setAllocateTx(null)}
        onOk={() => allocateForm.submit()}
        okText="Alocă"
        cancelText="Renunță"
        okButtonProps={{ disabled: !selectedExpenseId }}
        confirmLoading={allocateMutation.isPending}
        width={760}
        forceRender
      >
        {allocateTx ? (
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {allocateTx.description ?? allocateTx.counterpartyName} — <MoneyText value={allocateTx.remainingRon} /> disponibil
          </Text>
        ) : null}
        <div style={{ marginBottom: 8 }}>
          <Text strong>Alege cheltuiala</Text>
        </div>
        <Input.Search
          placeholder="Caută după număr, furnizor sau descriere"
          allowClear
          style={{ marginBottom: 8 }}
          onSearch={setExpenseSearch}
        />
        <Table<ExpenseListItem>
          rowKey="id"
          size="small"
          columns={expenseColumns}
          dataSource={expensesPage?.items ?? []}
          pagination={false}
          scroll={{ y: 260 }}
          rowClassName={(row) => (row.id === selectedExpenseId ? 'ant-table-row-selected' : '')}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedExpenseId ? [selectedExpenseId] : [],
            onChange: (keys) => setSelectedExpenseId(keys[0] as string | undefined),
          }}
          onRow={(row) => ({ style: { cursor: 'pointer' }, onClick: () => setSelectedExpenseId(row.id) })}
          locale={{ emptyText: 'Nicio cheltuială — caută sau creează una nouă.' }}
        />
        <Form<AllocateFormValues> form={allocateForm} layout="vertical" onFinish={(values) => allocateMutation.mutate(values)} style={{ marginTop: 12 }}>
          <Form.Item name="amountRon" label="Sumă alocată (RON)" rules={[{ required: true, message: 'Suma este obligatorie.' }]}>
            <MoneyInput style={{ maxWidth: 220 }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Cheltuială nouă din tranzacție"
        open={createTx !== null}
        onCancel={() => setCreateTx(null)}
        onOk={() => createForm.submit()}
        okText="Creează și alocă"
        cancelText="Renunță"
        confirmLoading={quickCreateMutation.isPending}
        forceRender
      >
        {createTx ? (
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            <DateText value={createTx.transactionDate} /> — <MoneyText value={createTx.remainingRon} /> —{' '}
            {createTx.counterpartyName ?? createTx.description}
          </Text>
        ) : null}
        <Form<QuickCreateFormValues> form={createForm} layout="vertical" onFinish={(values) => quickCreateMutation.mutate(values)}>
          <Form.Item name="kind" label="Tip" rules={[{ required: true }]}>
            <Select
              options={(Object.keys(KIND_LABELS) as ExpenseKind[]).map((kind) => ({ value: kind, label: KIND_LABELS[kind] }))}
            />
          </Form.Item>
          <Form.Item name="categoryId" label="Categorie">
            <Select
              allowClear
              placeholder="Fără categorie"
              options={(categories ?? []).map((category) => ({ value: category.id, label: category.name }))}
            />
          </Form.Item>
          <Form.Item name="description" label="Descriere">
            <Input maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
}
